import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ReportShell } from "@/features/reports/ReportShell";
import { useBranch } from "@/context/BranchContext";
import type { ReportFilters } from "@/features/reports/types";
import { Loader2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/integrations/api/client";

export const Route = createFileRoute("/_authenticated/reports/spare-stock-reorder")({
  component: SpareStockReorderReport,
});

function SpareStockReorderReport() {
  const [filters, setFilters] = useState<ReportFilters>({});
  const { currentBranchId } = useBranch();
  
  const { data, isLoading } = useQuery({
    queryKey: ["reports", "spare-stock-reorder", currentBranchId],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (currentBranchId) p.set("branch", currentBranchId);
      const res = await apiGet(`/reports/spare-stock-reorder/?${p.toString()}`);
      return res as any[];
    }
  });

  const exportRows = useMemo(() => () => (data ?? []).map((r) => ({
    "Spare Name": r.name,
    "Part Number": r.part_number,
    "Total Stock": r.total_stock,
    "Reorder Level": r.reorder_level,
    "30-Day Consumption": r.consumption_30d,
    "Status": r.below_reorder ? "Below Reorder" : "OK",
  })), [data]);

  return (
    <ReportShell
      id="R-21"
      title="Spare Stock, Reorder & Consumption Report"
      description="On-hand, reorder levels, valuation and consumption by spare."
      filters={filters}
      onFiltersChange={setFilters}
      onExport={exportRows}
      exportFilename="Spare_Stock_Reorder"
      allowedFilters={["branchId"]}
    >
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Spare Name</TableHead>
                  <TableHead>Part No</TableHead>
                  <TableHead className="text-right">Total Stock</TableHead>
                  <TableHead className="text-right">Reorder Level</TableHead>
                  <TableHead className="text-right">30-Day Consumption</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground">No data found</TableCell></TableRow>
                )}
                {data?.map((r: any) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell>{r.part_number || "—"}</TableCell>
                    <TableCell className="text-right">{r.total_stock}</TableCell>
                    <TableCell className="text-right">{r.reorder_level}</TableCell>
                    <TableCell className="text-right">{r.consumption_30d}</TableCell>
                    <TableCell>
                      {r.below_reorder ? (
                        <Badge variant="destructive">Low Stock</Badge>
                      ) : (
                        <Badge variant="secondary">OK</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </ReportShell>
  );
}
